import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { CFR_CATEGORIES } from "../../constants/cfrCategories";

const PRIORITY_COLORS = {
  critical: { bg: '#fee2e2', color: '#991b1b' },
  core: { bg: '#fef3c7', color: '#92400e' },
  supplemental: { bg: '#e0f2fe', color: '#075985' }
};

const ORG_TYPES = [
  { value: 'all', label: 'All causes' },
  { value: 'homeless_shelter', label: 'Homeless shelter' },
  { value: 'youth_center', label: 'Youth center' },
  { value: 'diaper_bank', label: 'Diaper bank' },
  { value: 'domestic_violence', label: 'Domestic violence' },
  { value: 'transitional_housing', label: 'Transitional housing' },
  { value: 'food_pantry', label: 'Food pantry' },
  { value: 'family_services', label: 'Family services' },
  { value: 'other', label: 'Other nonprofit' }
];

function BrowsePublic({ needType = 'supply' }) {
  const [selectedNeedType, setSelectedNeedType] = useState(needType);
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { addToCart, removeFromCart, isInCart } = useCart();

  const [needs, setNeeds] = useState([]);
  const [orgs, setOrgs] = useState({});
  const [loading, setLoading] = useState(true);
  const [priority, setPriority] = useState('all');
  const [priceRange, setPriceRange] = useState('all');
  const [frequency, setFrequency] = useState('all');
  const [orgType, setOrgType] = useState('all');
  const [cfrFilter, setCfrFilter] = useState('all');
  const [sortBy, setSortBy] = useState('priority');

  useEffect(() => { fetchData(); }, [selectedNeedType]);

  async function fetchData() {
    try {
      const [needsSnap, orgsSnap] = await Promise.all([
        getDocs(query(collection(db, 'needs'), where('status', '==', 'open'))),
        getDocs(query(collection(db, 'organizations'), where('status', '==', 'active')))
      ]);

      const all = needsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(n => n.isParent !== true)
        .filter(n => (n.type || 'supply') === selectedNeedType);
      const orgMap = {};
      orgsSnap.docs.forEach(d => { orgMap[d.id] = d.data(); });
      setNeeds(all);
      setOrgs(orgMap);
    } catch (err) {
      console.error('Error fetching needs:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleTakeResponsibility(need) {
  const actualType = need.type || 'supply';

  if (!userProfile) {
    const reason = actualType === 'sponsorship'
      ? 'sponsorship_signup_required'
      : 'signup_required';

    navigate(`/give/signup?need=${need.id}&item=${encodeURIComponent(need.itemName)}&org=${encodeURIComponent(need.orgName)}&reason=${reason}`);
    return;
  }

  if (actualType === 'sponsorship') {
    navigate(`/give/sponsorships/${need.id}/commit`);
    return;
  }

  if (isInCart(need.id)) {
    removeFromCart(need.id);
  } else {
    addToCart(need);
  }
}

  function getFiltered() {
    let result = [...needs];

    if (priority !== 'all') result = result.filter(n => n.priority === priority);

    if (priceRange !== 'all') {
      result = result.filter(n => {
        const p = n.pricePerCycle || 0;
        if (priceRange === 'under15') return p < 15;
        if (priceRange === '15to30') return p >= 15 && p <= 30;
        if (priceRange === '30to50') return p > 30 && p <= 50;
        if (priceRange === 'over50') return p > 50;
        return true;
      });
    }

    if (frequency !== 'all') result = result.filter(n => n.frequency === frequency);

    if (orgType !== 'all') {
      result = result.filter(n => { const org = orgs[n.orgId]; return org && org.orgType === orgType; });
    }

    if (cfrFilter !== 'all') {
      result = result.filter(n => (n.cfrCategories || []).includes(cfrFilter));
    }

    const priorityOrder = { critical: 1, core: 2, supplemental: 3 };
    result.sort((a, b) => {
      if (sortBy === 'priority') {
        const pDiff = (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99);
        if (pDiff !== 0) return pDiff;
        return (a.pricePerCycle || 999) - (b.pricePerCycle || 999);
      }
      if (sortBy === 'price_low') return (a.pricePerCycle || 0) - (b.pricePerCycle || 0);
      if (sortBy === 'price_high') return (b.pricePerCycle || 0) - (a.pricePerCycle || 0);
      if (sortBy === 'newest') return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      return 0;
    });

    return result;
  }

  const isSponsorshipPage = selectedNeedType === 'sponsorship';
  const pageTitle = isSponsorshipPage ? 'Open sponsorships in Springfield' : 'Open needs in Springfield';
  const pageDescription = isSponsorshipPage
    ? 'These sponsorship opportunities come from vetted Springfield nonprofits. Sign up to take responsibility and be connected directly with the organization.'
    : 'Each of these is a real, recurring need from a vetted Springfield nonprofit. Pick one and take responsibility.';
  const resultLabel = isSponsorshipPage ? 'sponsorship' : 'need';
  const filtered = getFiltered();
  const activeFilterCount = [priority !== 'all', priceRange !== 'all', frequency !== 'all', orgType !== 'all', cfrFilter !== 'all'].filter(Boolean).length;

  function resetFilters() {
    setPriority('all'); setPriceRange('all'); setFrequency('all');
    setOrgType('all'); setCfrFilter('all'); setSortBy('priority');
  }

  const btnStyle = (active) => ({
    padding: '0.4rem 0.875rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: 600,
    cursor: 'pointer', border: active ? 'none' : '1px solid #ddd',
    background: active ? '#2d6a4f' : 'white', color: active ? 'white' : '#555', whiteSpace: 'nowrap'
  });

  const selectStyle = {
    padding: '0.4rem 0.75rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: 600,
    cursor: 'pointer', border: '1px solid #ddd', background: 'white', color: '#555',
    appearance: 'none', paddingRight: '1.5rem'
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif' }}>

      <header style={{ background: 'white', borderBottom: '1px solid #eee', padding: '0 2rem', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#2d6a4f', cursor: 'pointer' }} onClick={() => navigate('/')}>somebody.gives</div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem', color: '#555' }}>📍 Springfield, MO</span>
          <button onClick={() => navigate('/give/login')} style={{ background: 'transparent', color: '#2d6a4f', border: '2px solid #2d6a4f', padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.9rem', cursor: 'pointer', fontWeight: 600 }}>Sign in</button>
          <button onClick={() => navigate('/give/signup')} style={{ background: '#2d6a4f', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.9rem', cursor: 'pointer', fontWeight: 600 }}>Get started</button>
        </div>
      </header>

      <main style={{ maxWidth: '900px', margin: '0 auto', padding: '2.5rem 2rem' }}>

        <div style={{ marginBottom: '1.5rem' }}>
  <h1 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: '0.5rem' }}>{pageTitle}</h1>
  <p style={{ color: '#555', fontSize: '0.95rem' }}>{pageDescription}</p>

  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
    <button
      onClick={() => setSelectedNeedType('supply')}
      style={{
        padding: '0.5rem 1rem',
        borderRadius: '999px',
        border: 'none',
        cursor: 'pointer',
        fontWeight: 700,
        background: selectedNeedType === 'supply' ? '#2d6a4f' : '#e5e7eb',
        color: selectedNeedType === 'supply' ? 'white' : '#111827'
      }}
    >
      Supply Needs
    </button>

    <button
      onClick={() => setSelectedNeedType('sponsorship')}
      style={{
        padding: '0.5rem 1rem',
        borderRadius: '999px',
        border: 'none',
        cursor: 'pointer',
        fontWeight: 700,
        background: selectedNeedType === 'sponsorship' ? '#2d6a4f' : '#e5e7eb',
        color: selectedNeedType === 'sponsorship' ? 'white' : '#111827'
      }}
    >
      Sponsorships
    </button>
  </div>
</div>

        <div style={{ background: 'white', borderRadius: '12px', padding: '1rem 1.25rem', border: '1px solid #eee', marginBottom: '1.5rem' }}>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#888', marginRight: '0.25rem' }}>Priority:</span>
            {['all', 'critical', 'core', 'supplemental'].map(f => (
              <button key={f} onClick={() => setPriority(f)} style={btnStyle(priority === f)}>{f === 'all' ? 'All' : f}</button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#888', marginRight: '0.25rem' }}>Price:</span>
            {[
              { value: 'all', label: 'Any price' }, { value: 'under15', label: 'Under $15' },
              { value: '15to30', label: '$15-$30' }, { value: '30to50', label: '$30-$50' }, { value: 'over50', label: 'Over $50' }
            ].map(f => (
              <button key={f.value} onClick={() => setPriceRange(f.value)} style={btnStyle(priceRange === f.value)}>{f.label}</button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#888', marginRight: '0.25rem' }}>Frequency:</span>
            {[
              { value: 'all', label: 'Any' }, { value: 'monthly', label: 'Monthly' },
              { value: 'bi-monthly', label: 'Bi-monthly' }, { value: 'quarterly', label: 'Quarterly' }, { value: 'one-time', label: 'One-time' }
            ].map(f => (
              <button key={f.value} onClick={() => setFrequency(f.value)} style={btnStyle(frequency === f.value)}>{f.label}</button>
            ))}
          </div>

          {/* Community Priority (CFR) filter */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#888', marginRight: '0.25rem' }}>Community Priority (CFR):</span>
            <button onClick={() => setCfrFilter('all')} style={btnStyle(cfrFilter === 'all')}>All</button>
            {CFR_CATEGORIES.map(cat => (
              <button key={cat.value} onClick={() => setCfrFilter(cat.value)} style={btnStyle(cfrFilter === cat.value)}>{cat.label}</button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#888', marginRight: '0.25rem' }}>Cause:</span>
            <select value={orgType} onChange={e => setOrgType(e.target.value)} style={selectStyle}>
              {ORG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>

            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#888', margin: '0 0.25rem 0 0.75rem' }}>Sort:</span>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={selectStyle}>
              <option value="priority">Priority first</option>
              <option value="price_low">Price: low to high</option>
              <option value="price_high">Price: high to low</option>
              <option value="newest">Newest first</option>
            </select>

            {activeFilterCount > 0 && (
              <button onClick={resetFilters} style={{ ...btnStyle(false), color: '#dc2626', border: '1px solid #dc2626' }}>
                Clear filters ({activeFilterCount})
              </button>
            )}
          </div>
        </div>

        {!loading && (
          <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '1rem' }}>
            {filtered.length} {resultLabel}{filtered.length !== 1 ? 's' : ''} found
          </div>
        )}

        {loading && <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>Loading needs...</div>}

        {!loading && filtered.map(need => {
          const needCfr = (need.cfrCategories || [])
            .map(val => CFR_CATEGORIES.find(c => c.value === val))
            .filter(Boolean);

          return (
            <div key={need.id} style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', border: '1px solid #eee', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>

              <div style={{ flex: 1, display: 'flex', gap: '0.875rem', alignItems: 'flex-start' }}>
                {orgs[need.orgId]?.logoUrl ? (
                  <img src={orgs[need.orgId].logoUrl} alt={`${need.orgName} logo`} style={{ width: '52px', height: '52px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0, border: '1px solid #e5e7eb', background: 'white' }} />
                ) : (
                  <div style={{ width: '52px', height: '52px', borderRadius: '10px', flexShrink: 0, border: '1px solid #e5e7eb', background: '#eef2f7', color: '#2d6a4f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.1rem' }}>
                    {(need.orgName || '?').charAt(0)}
                  </div>
                )}

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ background: PRIORITY_COLORS[need.priority]?.bg, color: PRIORITY_COLORS[need.priority]?.color, padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>
                      {need.priority}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#888' }}>{need.frequency}</span>
                    <span style={{ fontSize: '0.8rem', color: '#2d6a4f', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate(`/o/${need.orgId}`)}>
                      {need.orgName}
                    </span>
                  </div>

                  <div style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.35rem' }}>{need.itemName}</div>
                  <div style={{ fontSize: '0.875rem', color: '#555', marginBottom: needCfr.length > 0 ? '0.4rem' : 0 }}>{need.impactStatement}</div>

                  {needCfr.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                      {needCfr.map(cat => (
                        <span key={cat.value} style={{ padding: '0.15rem 0.55rem', borderRadius: '20px', fontSize: '0.68rem', fontWeight: 600, background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd' }}>
                          {cat.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ textAlign: 'right', minWidth: '160px', flexShrink: 0 }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#2d6a4f', marginBottom: '0.25rem' }}>
                  ${Math.ceil(need.pricePerCycle || 0)}
                  <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#888' }}>/{need.frequency}</span>
                </div>

                <button onClick={() => handleTakeResponsibility(need)} style={{ background: isInCart(need.id) ? '#2d6a4f' : '#1e3a5f', color: 'white', border: 'none', padding: '0.6rem 1.25rem', borderRadius: '8px', fontSize: '0.9rem', cursor: 'pointer', fontWeight: 600, width: '100%', marginTop: '0.25rem' }}>
                  {isSponsorshipPage ? 'Take responsibility' : isInCart(need.id) ? '✓ In cart' : 'Take responsibility'}
                </button>

                <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.35rem' }}>
                  {isSponsorshipPage ? 'Signup required' : userProfile ? 'Added to your cart' : 'Signup required'}
                </div>
              </div>
            </div>
          );
        })}

      </main>
    </div>
  );
}

export default BrowsePublic;
